<template>
  <div class="aiw2-title">
    <hero title="Title" :keyword="keyword" :mode="mode" :page-run="pageRun"
      info-title="Your page title is what people see in Google results, and your H1 is the big heading at the top of the page. POP writes several options; you pick one." />

    <!-- Approach config -->
    <div class="title-config">
      <div class="title-config-head">
        How should POP write your titles?
        <v-tooltip top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="info-ico" v-bind="attrs" v-on="on"></span>
          </template>
          <div class="white-bg-tooltip small-tooltip">Pick whether POP writes new titles for you or leaves your existing ones alone. Generating gives you four options in different styles to choose from.</div>
        </v-tooltip>
      </div>

      <label class="radio-row" :class="{ selected: approach === 'generate' }" @click="approach = 'generate'">
        <span class="radio-dot"></span>
        <span><strong>Let POP generate new titles for me</strong> - written from your brief and POP recommendations.</span>
        <span class="recommend-badge">Recommended</span>
      </label>

      <label class="radio-row" :class="{ selected: approach === 'example' }" @click="approach = 'example'">
        <span class="radio-dot"></span>
        <span><strong>Give the AI an example title for inspiration</strong> - POP writes new titles in a similar style.</span>
      </label>

      <div v-if="approach === 'example'" class="title-example-fields">
        <div class="field-row">
          <label class="field-label">Example search engine title</label>
          <input class="text-input" v-model="exampleSeo" placeholder="e.g. How to Get to the Dolomites (Complete 2026 Guide)" />
        </div>
        <div class="field-row">
          <label class="field-label">Example page title (H1)</label>
          <input class="text-input" v-model="examplePageTitle" placeholder="e.g. Getting to the Dolomites: Every Route Explained" />
        </div>
      </div>

      <label class="radio-row" :class="{ selected: approach === 'leave', disabled: pageNotBuilt }" @click="!pageNotBuilt && (approach = 'leave')">
        <span class="radio-dot"></span>
        <span>I don't want to generate new optimized search engine and page title with AI <strong>(leave as is)</strong></span>
        <v-tooltip top color="transparent">
          <template v-slot:activator="{ on, attrs }">
            <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
          </template>
          <div class="white-bg-tooltip small-tooltip">{{ pageNotBuilt ? "Your page isn't built yet, so there's no existing title to keep." : "Keep the title you already have. POP won't write a new one and won't change your page heading either. Useful when the title is already agreed or published. You can still edit it by hand later." }}</div>
        </v-tooltip>
      </label>

      <template v-if="approach !== 'leave'">
        <div class="title-config-toggles">
          <div class="tct-label">Extra inspiration</div>
          <div class="toggle-row" @click="useExistingTitle = !useExistingTitle">
            <button type="button" class="toggle" :class="{ on: useExistingTitle }"></button>
            <span>Use my existing title for inspiration</span>
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
              </template>
              <div class="white-bg-tooltip small-tooltip">POP reads your current title first and writes new options in a similar style, so they still sound like you. Turn this off if you want a completely fresh direction.</div>
            </v-tooltip>
          </div>
          <div class="toggle-row" @click="useCompetitorTitles = !useCompetitorTitles">
            <button type="button" class="toggle" :class="{ on: useCompetitorTitles }"></button>
            <span>Also use my competitors' titles for inspiration</span>
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
              </template>
              <div class="white-bg-tooltip small-tooltip">POP looks at the titles of pages already ranking for your keyword and borrows what works: the angle, the length, the phrasing. It won't copy them; it writes original titles that compete with them.</div>
            </v-tooltip>
          </div>
        </div>
      </template>

      <!-- Advanced stays visible in every approach, including "leave as is" -->
      <details class="adv-sub" style="margin-top: 4px;">
        <summary>Advanced</summary>
        <div style="padding: 10px 0 2px;">
          <div class="toggle-row" @click="toggleSameText">
            <button type="button" class="toggle" :class="{ on: sameText }"></button>
            <span>Use same text for SEO title and H1</span>
            <v-tooltip top color="transparent">
              <template v-slot:activator="{ on, attrs }">
                <span class="info-ico" v-bind="attrs" v-on="on" @click.stop></span>
              </template>
              <div class="white-bg-tooltip small-tooltip">Most pages use the same wording for the Google result and the heading on the page, which is simplest. Turn this off when you want a shorter, keyword-led title in search and a friendlier heading for readers.</div>
            </v-tooltip>
          </div>
        </div>
      </details>

      <template v-if="approach !== 'leave'">
        <div class="title-gen-bar">
          <button class="aiw2-btn aiw2-btn--primary" :disabled="generating" @click="generate">
            <span v-if="generating" class="spin"></span>
            <svg v-else-if="hasGenerated" class="gen-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
            <svg v-else class="gen-ico" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.09 5.91L20 10l-5.91 2.09L12 18l-2.09-5.91L4 10l5.91-2.09z"/></svg>
            {{ hasGenerated ? 'Regenerate titles' : 'Generate titles' }}
          </button>
          <span v-if="generateError" class="gen-error">{{ generateError }}</span>
        </div>

        <div v-if="generating" class="title-progress">
          <div class="title-progress-label"><span class="prog-glyph">✦</span> POP is writing title options…</div>
          <div class="title-progress-track"><div class="title-progress-fill" :style="{ width: genPct + '%' }"></div></div>
        </div>
      </template>
    </div>

    <!-- Results -->
    <div v-if="hasGenerated && approach !== 'leave'" class="title-results">
      <div class="title-results-label">Title options</div>
      <title-variant-card
        v-for="variant in variants"
        :key="variant.variant_id"
        :variant="variant"
        :selected="variant.variant_id === selectedVariantId"
        @select="selectVariant"
      />

      <div class="title-refine-bar">
        <span class="lbl">Refine</span>
        <button v-for="chip in refineChips" :key="chip.key" class="refine-chip" @click="openCreditModal(chip)">{{ chip.label }}</button>
        <button class="refine-chip refine-regen" @click="openRefineModal">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:5px;"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
          Regenerate
        </button>
      </div>

      <div class="outline-builder">
        <div class="outline-builder-head">SEO title &amp; page H1</div>
        <div class="outline-row">
          <span class="outline-tag google">T</span>
          <span class="text" contenteditable="true" @blur="outlineSeo = $event.target.textContent; syncH1()">{{ outlineSeo }}</span>
          <button class="outline-copy" @click="copy(outlineSeo)" title="Copy SEO title">
            <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
          </button>
        </div>
        <div class="outline-row">
          <span class="outline-tag h1">H1</span>
          <span class="text" :contenteditable="!sameText" @blur="outlineH1 = $event.target.textContent">{{ outlineH1 }}</span>
          <button class="outline-copy" @click="copy(outlineH1)" title="Copy H1">
            <svg viewBox="0 0 24 24"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V5a2 2 0 0 1 2-2h10"/></svg>
          </button>
        </div>
        <div class="outline-actions">
          <button class="outline-action" title="Regenerate" @click="openRefineModal">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
          </button>
          <button class="outline-action" title="View history" @click="openHistory">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/></svg>
          </button>
          <button class="outline-action" title="Download title" @click="downloadTitle">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/></svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Regenerate titles modal (matches the "Regenerate this section" design) -->
    <div v-if="regenConfirm.show" class="regen-overlay" @click.self="regenConfirm.show = false">
      <div class="regen-modal regen-again" role="dialog" aria-modal="true">
        <div class="ra-head">
          <h3>Regenerate titles</h3>
          <button class="ra-close" aria-label="Close" @click="regenConfirm.show = false">×</button>
        </div>
        <label class="ra-label">What would you like POP to change?</label>
        <textarea class="ra-input" rows="3" v-model="regenConfirm.text"
          placeholder="e.g. lead with the destination and keep them under 60 characters"></textarea>
        <div class="ra-sugg-label">Or pick a quick improvement</div>
        <div class="ra-suggs">
          <button v-for="s in refineChips" :key="s.key" class="ra-sugg"
            :class="{ active: regenConfirm.picked.indexOf(s.instruction) !== -1 }"
            @click="toggleSugg(regenConfirm, s.instruction)">{{ s.label }}</button>
        </div>
        <div v-if="refineError" class="err">{{ refineError }}</div>
        <div class="ra-foot">
          <div class="ra-cost">
            <span class="ra-cost-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span>
            <span v-if="unlimitedRegen">Regenerates are free on your account</span>
            <span v-else-if="paidRegenFree">Free — this article uses your own API key</span>
            <span v-else>Costs <strong>1 regenerate credit</strong> · {{ freeRemaining }} free left</span>
          </div>
          <div class="ra-actions">
            <button class="btn-cancel" @click="regenConfirm.show = false">Cancel</button>
            <button class="btn-regen" :disabled="generating" @click="confirmRegenerate">{{ paidRegenFree ? 'Regenerate' : 'Regenerate · 1 credit' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Credit confirm modal (refine chips) -->
    <div v-if="creditModal.show" class="regen-overlay" @click.self="creditModal.show = false">
      <div class="regen-modal credit-modal" role="dialog" aria-modal="true">
        <div class="credit-modal-icon"><svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></div>
        <h3>Use 1 regenerate credit?</h3>
        <p class="credit-modal-text">Making your titles <strong>{{ creditModal.chip && creditModal.chip.desc }}</strong> will use
          <strong>1 regenerate credit</strong>. You have <strong>{{ freeRemaining }} free regenerate credits</strong> left this report.</p>
        <div v-if="refineError" class="gen-error">{{ refineError }}</div>
        <div class="regen-actions">
          <button class="btn-cancel" @click="creditModal.show = false">Cancel</button>
          <button class="btn-regen" :disabled="refining" @click="confirmCreditRefine">Use 1 credit</button>
        </div>
      </div>
    </div>

    <!-- Refine titles modal (matches the "Regenerate this section" design) -->
    <div v-if="instructionModal.show" class="regen-overlay" @click.self="instructionModal.show = false">
      <div class="regen-modal regen-again" role="dialog" aria-modal="true">
        <div class="ra-head">
          <h3>Refine titles</h3>
          <button class="ra-close" aria-label="Close" @click="instructionModal.show = false">×</button>
        </div>
        <label class="ra-label">What would you like POP to change?</label>
        <textarea class="ra-input" rows="3" v-model="instructionModal.text"
          placeholder="Give instructions on what to add or avoid to make the output better."></textarea>
        <div class="ra-sugg-label">Or pick a quick improvement</div>
        <div class="ra-suggs">
          <button v-for="s in refineChips" :key="s.key" class="ra-sugg"
            :class="{ active: instructionModal.picked.indexOf(s.instruction) !== -1 }"
            @click="toggleSugg(instructionModal, s.instruction)">{{ s.label }}</button>
        </div>
        <div v-if="refineError" class="err">{{ refineError }}</div>
        <div class="ra-foot">
          <div class="ra-cost">
            <span class="ra-cost-ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M23 4v6h-6"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg></span>
            <span v-if="unlimitedRegen">Regenerates are free on your account</span>
            <span v-else-if="paidRegenFree">Free — this article uses your own API key</span>
            <span v-else>Costs <strong>1 regenerate credit</strong> · {{ freeRemaining }} free left</span>
          </div>
          <div class="ra-actions">
            <button class="btn-cancel" @click="instructionModal.show = false">Cancel</button>
            <button class="btn-regen" :disabled="refining" @click="confirmInstructionRefine">{{ paidRegenFree ? 'Regenerate' : 'Regenerate · 1 credit' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Page title history modal -->
    <history-modal
      variant="title"
      :show="historyModal.show"
      title="Page title history"
      intro="Every generate, refine and manual edit is saved with the exact title tag and H1 it produced. Load any version to put it back on the page."
      :items="historyModal.items"
      @close="historyModal.show = false"
      @restore="restoreTitleVersion"
    />
  </div>
</template>

<script>
import Hero from "../../components/Hero.vue";
import TitleVariantCard from "./TitleVariantCard.vue";
import HistoryModal from "../../components/HistoryModal.vue";
import {
  aiWriterTitleGenerate, aiWriterTitleRefine, aiWriterTitleSelect,
  aiWriterTitleLeaveAsIs, aiWriterGetProgress, aiWriterGetState,
  aiWriterTitleHistory, aiWriterTitleRestore,
} from "@/api";
import { downloadAsHtml } from "@/components/FullPageSourceEditorV2/components/AiWriter/helper";

const REFINE_CHIPS = [
  { key: "premium", label: "Make more premium", desc: "more premium and editorial", instruction: "Make the titles more premium and editorial in tone." },
  { key: "seo", label: "Make more SEO-focused", desc: "more SEO-focused", instruction: "Make the titles more SEO-focused with stronger term coverage." },
  { key: "shorter", label: "Make shorter", desc: "shorter and more concise", instruction: "Make the titles shorter and more concise." },
  { key: "local", label: "Make more local", desc: "more locally focused", instruction: "Give the titles a stronger local emphasis." },
];

export default {
  name: "AiwV2TitleStep",
  components: { Hero, TitleVariantCard, HistoryModal },
  props: {
    aiRunId: { default: null },
    keyword: { type: String, default: "" },
    mode: { type: String, default: "" },
    pageRun: { type: Object, default: () => ({}) },
    planning: { type: Object, default: () => ({}) },
    initialTitle: { type: Object, default: () => ({}) },
    credits: { type: Object, default: () => ({}) },
  },
  data() {
    const title = this.initialTitle || {};
    const variants = title.variants || [];
    return {
      refineChips: REFINE_CHIPS,
      approach: "generate",
      exampleSeo: "",
      examplePageTitle: "",
      useExistingTitle: false,
      useCompetitorTitles: false,
      sameText: true,
      variants,
      selectedVariantId: (title.selected && title.selected.variant_id) || (variants[0] && variants[0].variant_id) || null,
      outlineSeo: (title.selected && title.selected.seo_title) || (variants[0] && variants[0].seo_title) || "",
      outlineH1: (title.selected && title.selected.page_h1) || (variants[0] && variants[0].page_h1) || "",
      generating: false,
      generateError: "",
      refining: false,
      refineError: "",
      busy: false,
      creditModal: { show: false, chip: null },
      regenConfirm: { show: false, text: "", picked: [] },
      instructionModal: { show: false, text: "", picked: [] },
      historyModal: { show: false, items: [] },
      _titlePollTimer: null,
      localFreeRemaining: null,
      genPct: 0,
      _genTrickle: null,
    };
  },
  created() {
    // Defensive: never land on the disabled "leave as is" for unbuilt pages.
    if (this.pageNotBuilt && this.approach === "leave") this.approach = "generate";
  },
  computed: {
    token() {
      return this.$store.getters.getToken;
    },
    // "Leave as is" needs an existing page title to keep — impossible on a
    // page-not-built report, so the option is disabled there.
    pageNotBuilt() {
      return !!(this.pageRun && this.pageRun.pageNotBuilt);
    },
    hasGenerated() {
      return this.variants.length > 0;
    },
    selectedVariant() {
      return this.variants.find((v) => v.variant_id === this.selectedVariantId) || null;
    },
    canUseTitle() {
      return !!this.outlineSeo;
    },
    freeRemaining() {
      if (this.localFreeRemaining != null) return this.localFreeRemaining;
      return this.credits && this.credits.regenerateRemaining != null ? this.credits.regenerateRemaining : 5;
    },
    // White-glove / admin accounts are never charged for regenerates, so don't
    // gate refines behind a "costs 1 credit" confirmation for them.
    unlimitedRegen() {
      return !!(this.credits && this.credits.unlimitedRegenerate);
    },
    // Own-key runs consume the 5 free regenerates first, then stay free — so
    // once the pool is empty, treat regenerates like the unlimited case.
    ownKeyFree() {
      return !!(this.credits && this.credits.ownKeyFree);
    },
    paidRegenFree() {
      return this.unlimitedRegen || (this.ownKeyFree && this.freeRemaining === 0);
    },
    // Shared bottom-bar config. "Leave as is" continues directly; otherwise the
    // user must have a resolved title (canUseTitle) before Next is enabled.
    barState() {
      if (this.approach === "leave") {
        return { ctaLabel: "Continue →", disabled: false, busy: this.busy, hideNext: false };
      }
      return { ctaLabel: "Use this title →", disabled: !this.canUseTitle, busy: this.busy, hideNext: false };
    },
    // The live (uncommitted) candidate title — updates on card selection
    // (syncOutline) and manual edits — so the sidebar can preview its term
    // coverage before the user commits.
    candidatePayload() {
      return { seoTitle: this.outlineSeo, pageH1: this.outlineH1 };
    },
  },
  watch: {
    barState: { immediate: true, handler(v) { this.$emit("bar", v); } },
    candidatePayload: { immediate: true, handler(v) { this.$emit("candidate", v); } },
    // Title variants in flight: the parent mirrors this into its Rocky snapshot.
    generating(v) { this.$emit("generating", v); },
  },
  beforeDestroy() {
    if (this._titlePollTimer) clearTimeout(this._titlePollTimer);
    clearInterval(this._genTrickle);
    // Leaving mid-generation must not leave the parent flagged as generating.
    if (this.generating) this.$emit("generating", false);
  },
  methods: {
    // The title job is a single opaque LLM call (server only reports 20% → 100%),
    // so ease a determinate bar toward a 90% cap while it runs; completion snaps
    // it to 100%. Start/complete are real; the middle is a smooth estimate.
    startGenProgress() {
      this.genPct = 8;
      clearInterval(this._genTrickle);
      this._genTrickle = setInterval(() => {
        this.genPct = Math.min(90, this.genPct + Math.max(0.5, (90 - this.genPct) * 0.05));
      }, 240);
    },
    stopGenProgress() {
      clearInterval(this._genTrickle);
      this._genTrickle = null;
    },
    // Invoked by the shared bottom bar's Next button (via Index $refs.activeStep).
    barNext() {
      if (this.approach === "leave") this.leaveAsIs();
      else this.useThisTitle();
    },
    toggleSameText() {
      this.sameText = !this.sameText;
      this.syncOutline();
    },
    generate() {
      // First generation is free and direct. Any later "Regenerate titles" opens
      // the rich modal so the user can steer it with an instruction/chips (the
      // modal shows the credit cost; white-glove accounts see it's free).
      if (!this.hasGenerated) return this.doGenerate(false);
      this.refineError = "";
      this.regenConfirm = { show: true, text: "", picked: [] };
    },
    confirmRegenerate() {
      this.regenConfirm.show = false;
      this.doGenerate(true, this.regenConfirm.text);
    },
    openRefineModal() {
      this.refineError = "";
      this.instructionModal = { show: true, text: "", picked: [] };
    },
    toggleSugg(modal, text) {
      const i = modal.picked.indexOf(text);
      if (i !== -1) { modal.picked.splice(i, 1); return; }
      modal.picked.push(text);
      modal.text = (modal.text ? modal.text.trim() + "\n" : "") + text;
    },
    // The progress bar renders near the top of the step; scroll up to it so the
    // user sees generation is running (the window doesn't scroll — the editor
    // pane does — so use scrollIntoView, not window.scrollTo).
    scrollTop() {
      this.$nextTick(() => {
        if (this.$el && this.$el.scrollIntoView) {
          this.$el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    async doGenerate(charge, instruction) {
      this.generating = true;
      this.startGenProgress();
      this.scrollTop();
      this.generateError = "";
      try {
        const res = await aiWriterTitleGenerate(this.token, this.aiRunId, 4, charge, instruction || "");
        if (res?.data?.status === "SUCCESS") {
          await this.pollTitle();
          // Refresh the sidebar's regenerate-credits count after a charged regen.
          this.$emit("credits-changed");
        } else {
          this.generateError = res?.data?.error || "Could not generate titles.";
          this.stopGenProgress();
          this.generating = false;
        }
      } catch (e) {
        this.generateError = "Could not generate titles.";
        this.stopGenProgress();
        this.generating = false;
      }
    },
    openCreditModal(chip) {
      this.refineError = "";
      // No charge for unlimited (white-glove) accounts or own-key runs past
      // the free pool — refine directly.
      if (this.paidRegenFree) {
        this.doRefine(chip.instruction);
        return;
      }
      this.creditModal = { show: true, chip };
    },
    async confirmCreditRefine() {
      await this.doRefine(this.creditModal.chip.instruction);
    },
    async confirmInstructionRefine() {
      // Empty instruction is valid — "regenerate without specific guidance".
      await this.doRefine(this.instructionModal.text);
    },
    async doRefine(instruction) {
      // Behave like generate: close the refine modal, scroll to the top, and show
      // the main progress bar while the refine runs. Errors surface at the top
      // (generateError) since the modal — which held the refine error — is closed.
      this.creditModal.show = false;
      this.instructionModal.show = false;
      this.refining = true;
      this.generating = true;
      this.startGenProgress();
      this.scrollTop();
      this.generateError = "";
      this.refineError = "";
      try {
        const res = await aiWriterTitleRefine(this.token, this.aiRunId, {
          instruction,
          currentVariantId: this.selectedVariantId,
          variantCount: 3,
          confirm: true,
        });
        if (res?.data?.status === "SUCCESS") {
          await this.pollTitle();
          this.instructionModal = { show: false, text: "", picked: [] };
          this.$emit("credits-changed");
        } else if (res?.data?.code === "insufficient_credits") {
          this.generateError = "You don't have enough credits to refine.";
          this.stopGenProgress();
          this.generating = false;
        } else {
          this.generateError = res?.data?.error || "Could not refine titles.";
          this.stopGenProgress();
          this.generating = false;
        }
      } catch (e) {
        this.generateError = "Could not refine titles.";
        this.stopGenProgress();
        this.generating = false;
      } finally {
        this.refining = false;
      }
    },
    pollTitle() {
      return new Promise((resolve) => {
        const tick = async () => {
          try {
            const res = await aiWriterGetProgress(this.token, this.aiRunId);
            const p = res?.data?.progress || {};
            if (p.phase === "title" && p.status === "complete") {
              this.genPct = 100;
              this.stopGenProgress();
              await this.reloadTitle();
              this.generating = false;
              return resolve(p);
            }
            if (p.phase === "title" && p.status === "failed") {
              this.stopGenProgress();
              this.generating = false;
              this.generateError = (p.error && p.error.message) || "Title generation failed.";
              this.refineError = this.generateError;
              return resolve(p);
            }
          } catch (e) { /* keep polling */ }
          this._titlePollTimer = setTimeout(tick, 1200);
        };
        this._titlePollTimer = setTimeout(tick, 800);
      });
    },
    async reloadTitle() {
      try {
        const res = await aiWriterGetState(this.token, this.aiRunId);
        const session = res?.data?.session || {};
        const title = session.title || {};
        this.variants = title.variants || [];
        if (!this.selectedVariant && this.variants.length) {
          this.selectedVariantId = this.variants[0].variant_id;
        }
        if (session.credits && session.credits.regenerateRemaining != null) {
          this.localFreeRemaining = session.credits.regenerateRemaining;
        }
        this.syncOutline();
      } catch (e) { /* ignore */ }
    },
    selectVariant(id) {
      this.selectedVariantId = id;
      this.syncOutline();
    },
    syncOutline() {
      const v = this.selectedVariant;
      if (!v) return;
      this.outlineSeo = v.seo_title;
      this.outlineH1 = this.sameText ? v.seo_title : v.page_h1;
    },
    syncH1() {
      if (this.sameText) this.outlineH1 = this.outlineSeo;
    },
    copy(text) {
      if (navigator.clipboard) navigator.clipboard.writeText(text || "");
    },
    async openHistory() {
      this.historyModal = { show: true, items: [] };
      try {
        const res = await aiWriterTitleHistory(this.token, this.aiRunId);
        this.historyModal.items = res?.data?.history || [];
      } catch (e) { /* ignore */ }
    },
    async restoreTitleVersion(versionId) {
      try {
        await aiWriterTitleRestore(this.token, this.aiRunId, versionId);
        this.historyModal.show = false;
        await this.reloadTitle();
      } catch (e) { /* ignore */ }
    },
    downloadTitle() {
      const html = `<title>${this.outlineSeo || ""}</title>\n<h1>${this.outlineH1 || ""}</h1>`;
      downloadAsHtml("title.html", html);
    },
    async useThisTitle() {
      this.busy = true;
      try {
        const res = await aiWriterTitleSelect(this.token, this.aiRunId, {
          variantId: this.selectedVariantId,
          seoTitle: this.outlineSeo,
          pageH1: this.outlineH1,
          useSameTitleAndH1: this.sameText,
        });
        if (res?.data?.status === "SUCCESS") {
          this.$emit("selected");
        }
      } finally {
        this.busy = false;
      }
    },
    async leaveAsIs() {
      this.busy = true;
      try {
        let res = await aiWriterTitleLeaveAsIs(this.token, this.aiRunId, {});
        // The source page (e.g. the URL being optimized) may need scraping first;
        // the backend does it async — poll the 'scrape' phase, then retry.
        if (res?.data?.status === "PROCESSING") {
          await this.pollScrape();
          res = await aiWriterTitleLeaveAsIs(this.token, this.aiRunId, {});
        }
        if (res?.data?.status === "SUCCESS") this.$emit("selected");
      } finally {
        this.busy = false;
      }
    },
    pollScrape() {
      return new Promise((resolve) => {
        const tick = async () => {
          try {
            const res = await aiWriterGetProgress(this.token, this.aiRunId);
            const p = res?.data?.progress || {};
            if (p.phase === "scrape" && (p.status === "complete" || p.status === "failed")) {
              return resolve(p);
            }
          } catch (e) { /* keep polling */ }
          this._titlePollTimer = setTimeout(tick, 1200);
        };
        this._titlePollTimer = setTimeout(tick, 800);
      });
    },
  },
};
</script>

<style scoped>
.aiw2-title { margin: 0 auto; }

/* ----- Title approach config ----- */
.title-config { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 20px; margin-bottom: 14px; }
.title-config-head { font-size: 13.5px; font-weight: 700; color: var(--text, #052e33); margin-bottom: 12px; display: flex; align-items: center; gap: 6px; }
.title-example-fields { display: flex; flex-direction: column; gap: 12px; margin: 4px 0 6px 28px; padding: 14px 16px; background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-sm, 6px); }
.title-config-toggles { margin-top: 10px; padding-top: 12px; border-top: 1px solid var(--border, #e4e4e4); }
.tct-label { font-size: 10.5px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-bottom: 8px; }
.title-gen-bar { display: flex; align-items: center; gap: 14px; margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border, #e4e4e4); }
/* CTA icon: an SVG (refresh when regenerating, sparkle otherwise) rather than a
   text glyph, so it renders at a predictable size instead of depending on the
   font's glyph metrics. */
.gen-ico { width: 14px; height: 14px; flex-shrink: 0; display: block; }
.gen-error { color: var(--pop-red, #ff3939); font-size: 12.5px; }
.title-progress { margin-top: 16px; }
.title-progress-label { font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751); display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.title-progress .prog-glyph { display: inline-block; animation: titleSpin 1s linear infinite; }
@keyframes titleSpin { to { transform: rotate(360deg); } }
.title-progress-track { height: 8px; border-radius: 999px; background: #e6e9f0; overflow: hidden; }
.title-progress-fill { height: 100%; width: 0; border-radius: 999px; background: var(--pop-orange, #ff5700); transition: width .3s ease; }

/* ----- Results ----- */
.title-results-label { font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-bottom: 10px; }
.title-refine-bar { display: flex; flex-wrap: wrap; gap: 6px; margin: 14px 0 16px; padding: 12px 14px; background: var(--surface, #fff); border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); }
.title-refine-bar .lbl { font-size: 11px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-right: 6px; align-self: center; }
.refine-chip { background: var(--surface-2, #f7f7f7); border: 1px solid var(--border, #e4e4e4); color: var(--text-2, #014751); padding: 5px 10px; border-radius: 6px; font-size: 12px; cursor: pointer; font-family: inherit; }
.refine-chip:hover { background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange-tint, #ffd9c2); }
.refine-regen { display: inline-flex; align-items: center; margin-left: auto; background: #fff; color: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange, #ff5700); font-weight: 700; }
.refine-regen:hover { background: var(--pop-orange-soft, #ffeddd); }

/* ----- Outline builder ----- */
.outline-builder { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); padding: 16px 20px; margin-top: 16px; }
.outline-builder-head { font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin-bottom: 12px; }
.outline-row { display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: 1px solid var(--border, #e4e4e4); border-radius: 7px; margin-bottom: 6px; background: var(--surface-2, #f7f7f7); font-size: 13.5px; color: var(--text, #052e33); }
.outline-tag { width: 24px; height: 24px; border-radius: 5px; background: #fff; border: 1px solid var(--border-strong, #ccc); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--text-2, #014751); flex-shrink: 0; }
.outline-tag.h1 { background: #fff; color: var(--pop-orange, #ff5700); border-color: var(--pop-orange, #ff5700); }
.outline-tag.google { background: #fff; color: var(--pop-blue, #316bff); border-color: var(--border, #e4e4e4); }
.outline-row .text { flex: 1; font-weight: 500; padding: 4px 6px; margin: -4px -6px; border-radius: 5px; cursor: text; border: 1px solid transparent; transition: background .12s, border-color .12s; outline: none; }
.outline-row .text:hover { background: #fff; }
.outline-row .text[contenteditable="true"]:focus { background: #fff; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.outline-copy { flex-shrink: 0; width: 28px; height: 28px; border-radius: 6px; background: transparent; border: 1px solid transparent; display: flex; align-items: center; justify-content: center; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 15px; transition: background .12s, color .12s, border-color .12s; }
.outline-row:hover .outline-copy { border-color: var(--border, #e4e4e4); }
.outline-copy:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }
.outline-copy svg { width: 15px; height: 15px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.outline-actions { display: flex; gap: 6px; margin-top: 10px; }
.outline-action { width: 28px; height: 28px; border-radius: 6px; background: #fff; border: 1px solid var(--border, #e4e4e4); display: flex; align-items: center; justify-content: center; color: var(--text-3, #6b7b7e); cursor: pointer; font-size: 14px; }
.outline-action:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }
.outline-action svg { width: 18px; height: 18px; display: block; }

/* ----- Regenerate / credit modals ----- */
.regen-overlay { position: fixed; inset: 0; z-index: 200; background: rgba(5,46,51,.55); display: flex; align-items: center; justify-content: center; padding: 24px; }
.regen-modal { background: #fff; border-radius: 16px; box-shadow: 0 24px 70px rgba(0,0,0,.35); width: 100%; max-width: 620px; padding: 28px 30px 24px; font-family: "Mulish", system-ui, sans-serif; }
.regen-modal h3 { font-size: 22px; font-weight: 800; color: var(--text, #052e33); margin: 0 0 18px; }
.regen-textarea { width: 100%; min-height: 160px; resize: vertical; border: 1px solid var(--border-strong, #ccc); border-radius: 10px; padding: 14px 16px; font-family: inherit; font-size: 14px; color: var(--text, #052e33); line-height: 1.5; box-sizing: border-box; }
.regen-textarea:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.regen-own { display: flex; align-items: center; gap: 12px; margin: 18px 0 14px; font-size: 14px; color: var(--text, #052e33); cursor: pointer; }
.regen-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 18px; }
.regen-actions button { font-family: inherit; font-size: 14px; font-weight: 700; padding: 11px 26px; border-radius: 999px; cursor: pointer; }
.btn-cancel { background: #fff; border: 1.5px solid var(--text, #052e33); color: var(--text, #052e33); }
.btn-cancel:hover { background: var(--surface-2, #f7f7f7); }
.btn-regen { background: var(--pop-orange, #ff5700); border: 1.5px solid var(--pop-orange, #ff5700); color: #fff; }
.btn-regen:hover { background: var(--pop-orange-deep, #b23d00); border-color: var(--pop-orange-deep, #b23d00); }
.btn-regen:disabled { background: var(--border, #e4e4e4); border-color: var(--border, #e4e4e4); color: var(--text-3, #6b7b7e); cursor: default; }
.regen-modal.credit-modal { max-width: 420px; text-align: center; padding: 30px 30px 24px; }
.credit-modal-icon { width: 52px; height: 52px; border-radius: 50%; background: var(--pop-orange-soft, #ffeddd); color: var(--pop-orange-deep, #b23d00); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; font-size: 22px; }
.credit-modal-text { font-size: 14px; color: var(--text-2, #014751); line-height: 1.55; margin: 0 0 22px; }

/* ----- Rich "Regenerate" modal — matches the DraftStep "Regenerate this section"
   design (.regen-modal.regen-again + .ra-*). ----- */
@keyframes regenPop { from { opacity: 0; transform: translateY(10px) scale(.98); } to { opacity: 1; transform: none; } }
.regen-modal.regen-again { max-width: 480px; padding: 22px 24px 20px; text-align: left; animation: regenPop .2s ease both; }
.regen-again .ra-head { display: flex; align-items: center; margin-bottom: 14px; }
.regen-again .ra-head h3 { font-family: inherit; margin: 0; font-size: 18px; font-weight: 800; color: var(--text, #052e33); }
.regen-again .ra-close { margin-left: auto; width: 30px; height: 30px; border: none; background: transparent; font-size: 22px; line-height: 1; color: var(--text-3, #6b7b7e); cursor: pointer; border-radius: 7px; }
.regen-again .ra-close:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }
.regen-again .ra-label { display: block; font-size: 12.5px; font-weight: 700; color: var(--text-2, #014751); margin-bottom: 7px; }
.regen-again .ra-input { width: 100%; box-sizing: border-box; resize: vertical; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); line-height: 1.5; border: 1px solid var(--border, #e4e4e4); border-radius: 9px; padding: 11px 13px; background: #fff; }
.regen-again .ra-input:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.regen-again .ra-sugg-label { font-size: 11px; font-weight: 700; letter-spacing: .03em; text-transform: uppercase; color: var(--text-3, #6b7b7e); margin: 16px 0 8px; }
.regen-again .ra-suggs { display: flex; flex-wrap: wrap; gap: 8px; }
.regen-again .ra-sugg { font-family: inherit; font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751); background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: 999px; padding: 7px 14px; cursor: pointer; transition: all .12s; }
.regen-again .ra-sugg:hover { background: var(--surface-2, #f7f7f7); border-color: var(--border-strong, #ccc); }
.regen-again .ra-sugg.active { background: var(--pop-orange, #ff5700); color: #fff; border-color: var(--pop-orange, #ff5700); }
.regen-again .err { color: var(--pop-red, #ff3939); font-size: 0.8rem; margin-top: 10px; }
.regen-again .ra-foot { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--border, #e4e4e4); }
.regen-again .ra-cost { font-size: 12.5px; color: var(--text-2, #014751); display: inline-flex; align-items: center; gap: 7px; }
.regen-again .ra-cost strong { color: inherit; font-weight: 700; }
.regen-again .ra-cost-ico { color: var(--pop-orange, #ff5700); line-height: 0; }
.regen-again .ra-cost-ico svg { width: 16px; height: 16px; }
.regen-again .ra-actions { margin-left: auto; display: inline-flex; gap: 8px; }
.regen-again .btn-cancel, .regen-again .btn-regen { font-family: inherit; font-size: 14px; font-weight: 700; padding: 11px 26px; border-radius: 999px; cursor: pointer; }
.regen-again .btn-regen:disabled { background: var(--border, #e4e4e4); border-color: var(--border, #e4e4e4); color: var(--text-3, #6b7b7e); cursor: default; }
.credit-modal .gen-error { margin-bottom: 12px; }

/* ----- Shared form primitives (scoped) ----- */
.info-ico { width: 16px; height: 16px; display: inline-block; flex-shrink: 0; cursor: pointer; background-color: #014751; -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; }
.help-text { font-size: 12px; color: var(--text-3, #6b7b7e); margin-top: 6px; line-height: 1.5; }
.field-row { display: flex; flex-direction: column; }
.field-label { display: block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: var(--text-3, #6b7b7e); margin-bottom: 8px; text-align: left; margin-right: 0; flex: none; }
.text-input { width: 100%; background: #fff; border: 1px solid var(--border-strong, #ccc); border-radius: 7px; padding: 9px 12px; font-family: inherit; font-size: 13.5px; color: var(--text, #052e33); transition: border-color .15s, box-shadow .15s; }
.text-input:focus { outline: none; border-color: var(--pop-orange, #ff5700); box-shadow: 0 0 0 3px var(--pop-orange-soft, #ffeddd); }
.radio-row { display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 7px; cursor: pointer; transition: background .15s; font-size: 13.5px; color: var(--text, #052e33); margin-bottom: 4px; }
.radio-row:hover { background: var(--surface-2, #f7f7f7); }
.radio-row.selected { background: var(--pop-orange-soft, #ffeddd); }
.radio-row.disabled { opacity: .55; cursor: not-allowed; }
.radio-row.disabled:hover { background: none; }
.radio-dot { width: 18px; height: 18px; border-radius: 50%; border: 1.5px solid var(--border-strong, #ccc); background: #fff; flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all .15s; }
.radio-row.selected .radio-dot { border-color: var(--pop-orange, #ff5700); }
.radio-row.selected .radio-dot::after { content: ""; width: 9px; height: 9px; border-radius: 50%; background: var(--pop-orange, #ff5700); }
.recommend-badge { margin-left: auto; font-size: 9px; font-weight: 700; letter-spacing: .04em; background: var(--pop-green, #25a97a); color: #fff; padding: 3px 7px; border-radius: 3px; text-transform: uppercase; white-space: nowrap; }
.toggle-row { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: var(--text-2, #014751); cursor: pointer; padding: 6px 0; }
.toggle { width: 36px; height: 20px; border-radius: 999px; background: var(--border-strong, #ccc); position: relative; cursor: pointer; transition: background .2s; flex-shrink: 0; border: none; padding: 0; }
.toggle::after { content: ""; position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: #fff; transition: transform .2s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
.toggle.on { background: var(--pop-green, #25a97a); }
.toggle.on::after { transform: translateX(16px); }
.adv-sub { border-top: 1px dashed var(--border, #e4e4e4); padding-top: 10px; margin-top: 12px; }
.adv-sub > summary { cursor: pointer; list-style: none; font-size: 12px; font-weight: 600; color: var(--text-3, #6b7b7e); letter-spacing: .02em; padding: 4px 0; display: inline-flex; align-items: center; gap: 6px; user-select: none; }
.adv-sub > summary::-webkit-details-marker { display: none; }
.adv-sub > summary::before { content: "▸"; font-size: 10px; transition: transform .15s; color: var(--text-3, #6b7b7e); }
.adv-sub[open] > summary::before { transform: rotate(90deg); }
.spin { width: 12px; height: 12px; border: 2px solid rgba(255,255,255,.5); border-top-color: #fff; border-radius: 50%; display: inline-block; animation: titleSpin .7s linear infinite; }
</style>
